import { createGlobalStyle } from 'styled-components';

/** @type {import('./theme.types').CustomTheme} */
const myTheme = {
  listsBorder: 'solid 1px #EAE9E9',
  gray: 'rgba(0, 0, 0, 0.65)',
  gray1: '#333',
  lightGray: 'rgba(0, 0, 0, 0.25)',
  green2: '#2f7a5c',
  green3: '#35b281',
  green4: '#26805c',
  green5: '#28664D',
  newGreen: '#2f943e',
  lightGreen: '#35b281',
  primaryColor: '#5556FF', //making the primary color explicit
  buttonPurple: '#975C8A',
  newPurple: '#7e4b72',
  buttonGreen: '#4FCB9A',
  pastelYellow: '#fdec96',
  buttonRed: '#ff5345',
  iconColor: '#5556FF',
  fontColor: '#51555c',
  newOrange: '#ec4414',
  lightBlueGray: '#d4d7d9',
  blueGrayColor: '#a0a4ab',
  darkBlueGrayTwo: '#233456',
  basicsError: '#F5222D',
  basicsWarning: '#faad14',
  shoreCiBlue: '#0099ff',
  fontFamily: 'Roboto, PingFang SC, "Segoe UI Emoji", Segoe UI Symbol',
  fontVariant: 'tabular-nums',
  colorError: '#F5222D',
  colorErrorBg: '#FFF1F0',
  colorSuccess: '#52C41A',
  colorSuccessBg: '#F6FFED',
  colorWarning: '#FAAD14',
  colorWarningBg: '#FFFBE6',
};

// mostly used to fix global layout breaks after upgrading antd to v4
export const GlobalStyle = createGlobalStyle`

/* Override gravity design system brand tokens to match the shore app palette */
.gv-activated {
  --color-primary: #5556FF;
  --color-state-form-active: #5556FF;
  --form-element-height: 32px;
  /* .gv-input defaults to --color-surface-form (grey), and tints to
     --color-surface-toned (also grey) on hover/focus - keep both white so
     fields stay white everywhere, including portalled modals that don't
     inherit from a specific form's DOM subtree. */
  --color-surface-form: var(--color-surface-default, #fff);
  --color-surface-toned: var(--color-surface-default, #fff);
  /* --color-button-fg-on-primary defaults to a dark color (right for the
     brand's default teal --color-primary above), but our purple override is
     dark enough itself to need light text/icons for contrast instead. */
  --color-button-fg-on-primary: var(--color-on-fixed-white);
}


.gv-activated .gv-modal .gv-modal-content,
.gv-activated .gv-modal.gv-modal-compact .gv-modal-content {
  padding: 30px;
}

.gv-activated .gv-modal .gv-modal-content .gv-modal-title {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: var(--size-md, 16px);
}


.gv-modal-content {
padding: 15px !important;
  width: 100% !important;
  max-width: 550px !important;
}


.gv-modal-confirm .gv-modal-content {
  width: auto !important;
  max-width: 420px !important;
}
.gv-modal-confirm .gv-modal-title {
  border-bottom: none !important;
  padding-bottom: 0 !important;
}


.gv-activated .gv-data-table {
  --color-state-hover: #fafafa;
  --color-state-hover-dim: #fafafa;
}

.gv-activated .customer-import-history-table-el {
  table-layout: fixed;
}

.gv-activated .customers-table-el {
  table-layout: fixed;
  transition: opacity 0.15s ease;
}
.gv-activated .customers-table-el.customers-table-loading {
  opacity: 0.5;
}

/* @shore/react-gravity@0.5.2's own CSS targets ".gv-pagination nav ul",
   assuming a wrapper div around a <nav> - but Pagination actually renders
   the classes directly on the <nav> itself, so none of that upstream
   styling (row layout, button sizing, hover/active states) ever matches.
   Recreate it here scoped to the real DOM shape. */
/* GvPhoneInput composes Autocomplete (dial code) + GeneralField (number)
   side by side since real PhoneField's country picker can't be swapped for
   a branded one - join them visually the same way real PhoneField's own
   two boxes are joined (split border-radius, no gap, left border only lit
   up on focus/hover of the second box). */
/* GvSelect/GvCountrySelect/GvPhoneInput use Autocomplete as a fixed-choice
   picker, not a free-text field - its built-in "clear to empty" button
   doesn't apply (there's no valid "no selection" state for these). */
.gv-activated .gv-select-autocomplete .gv-clear {
  display: none;
}

/* Autocomplete's own search text doubles as its arrow button's filter
   query, so showing the selected label there means clicking the arrow again
   re-filters to only matching options instead of showing the full list.
   The input stays permanently empty (see GvSelect/GvCountrySelect/
   GvPhoneInput) and this overlay shows the selection instead - hidden while
   the panel is open (aria-expanded="true" on the real input) so the user
   can see/type into the actual empty field underneath.
   The overlay sits behind .gv-input in stacking (z-index below it), not on
   top of it - previous versions of this rule put the overlay ON TOP and
   tried to inset it by exactly the border's width so it wouldn't paint over
   the border, but that measured inset didn't reliably land pixel-for-pixel
   on the border's real edge and clipped part of it. Putting .gv-input on
   top instead, with its own background made transparent, means its border
   always wins regardless - the overlay's opaque background still shows
   through .gv-input's (transparent) content area underneath it. */
.gv-activated .gv-select-autocomplete .gv-input.gv-with-addon {
  position: relative;
  z-index: 1;
  background: transparent;
}
.gv-activated .gv-select-autocomplete .gv-select-overlay {
  position: absolute;
  inset: 0 44px 0 0;
  display: flex;
  align-items: center;
  padding-left: var(--input-padding, 16px);
  background: var(--color-surface-form);
  color: var(--color-body-on-default);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
}
.gv-activated .gv-select-autocomplete:has(input[aria-expanded='true'])
  .gv-select-overlay {
  display: none;
}

.gv-activated .gv-input-phone {
  align-items: flex-start;
}
.gv-activated .gv-input-phone > :first-child .gv-input {
  border-radius: var(--border-radius) 0 0 var(--border-radius);
}
.gv-activated .gv-input-phone > :last-child .gv-input {
  border-radius: 0 var(--border-radius) var(--border-radius) 0;
  border-left: transparent;
}
.gv-activated .gv-input-phone > :last-child .gv-input:focus,
.gv-activated .gv-input-phone > :last-child .gv-input:hover {
  border-left: 1px solid var(--color-state-form-active);
}

.gv-activated .gv-pagination.table-panel-pagination {
  border-top: none;
  padding: 0;
  flex-flow: row nowrap;
  width: auto;
}
.gv-activated .gv-pagination.table-panel-pagination ul {
  display: flex;
  flex-wrap: nowrap;
  list-style: none;
  padding: 0;
  margin: 0;
}
.gv-activated .gv-pagination.table-panel-pagination li {
  display: block;
  height: var(--pagination-btn-size, 40px);
  width: var(--pagination-btn-size, 40px);
}
.gv-activated .gv-pagination.table-panel-pagination li > * {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  height: 100%;
  width: 100%;
}
.gv-activated .gv-pagination.table-panel-pagination li > *:not(span) {
  border-radius: var(--border-radius);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
}
.gv-activated .gv-pagination.table-panel-pagination li > *:not(span):hover {
  background-color: var(--color-state-hover);
}
.gv-activated .gv-pagination.table-panel-pagination li.gv-active > * {
  background-color: var(--color-primary);
  color: var(--color-on-fixed-white);
  pointer-events: none;
}
.gv-activated .gv-pagination.table-panel-pagination li.gv-disabled > * {
  color: var(--color-body-on-alternative);
  pointer-events: none;
}

.gv-activated .gv-tab-panel-fill.gv-panel-active {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.gv-activated .gv-pagination.table-panel-pagination li{
  height: 24px;
  width: 24px;
}

.gv-form-option-horizontal {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--size-sm);
}
.gv-form-option-horizontal .gv-label {
  min-width: 140px;
  flex-shrink: 0;
}

body{
  margin:0;
  font-family: "Roboto, PingFang SC, 'Segoe UI Emoji', Segoe UI Symbol";
}

.ant-tabs-content-holder,
.ant-tabs-tabpane,
.ant-tabs-content {
  background: transparent !important;
  background-color: transparent !important;
}
  .ant-tag-warning{
  color: #fa8c16 !important;
  border-color: #ffd591 !important;
  }
  .ant-menu-item{
  padding-left: 24px !important;
    margin-bottom: 8px !important;
    margin-top: 4px !important;
    padding-right: 16px !important;
    }
  #hubspot-messages-iframe-container {
    z-index: 500 !important;
  }

  .ant-form-item-explain {
    margin-top: 0;
  }

  .prod_type_select .ant-select-item-option-content {
    white-space: unset;
  }

  .ant-btn > .ant-btn-loading-icon {
      margin-right: 5px;
  }

  .checkout-success-modal{
   .ant-modal-confirm .ant-modal-body {
     padding: 0px !important;
   }
     .ant-modal-confirm-body{
     border-bottom: 1px solid rgba(0,0,0,.06) !important;
     padding-bottom: 5px;
     }

   .ant-modal-body {
     padding: 15px !important;
    }

    .ant-modal-confirm .ant-modal-confirm-btns{
      margin-top: 0px !important;
      border-top: 1px solid rgba(0,0,0,.06) !important;
    }
      .ant-modal-confirm-btns{
       margin-top: 5px !important;
      }
}
     .ant-alert{
     border-radius: 0px !important;
     }

     .ant-modal-container{
       padding: 0 !important;
       }
 .ant-modal-content{
       border-top: 1px solid rgba(0,0,0,.06) !important;
       margin: 0 auto !important;
       padding: 0px !important;
       border-radius: 0px !important;
    }
       .ant-modal-header{
       padding: 20px !important;
       border-bottom: 2px solid #f0f0f0 !important;
       }

       .ant-modal-footer{
       padding:10px  !important;
       border-top: 2px solid #f0f0f0 !important;
       }
       .ant-table-row-level-1 > td[class*='ant-table-cell']{
          background: #FAFAFA !important;
       }

       .ant-modal-confirm-body-wrapper{
       padding: 24px !important;

          .ant-modal-confirm-btns .ant-btn-primary {
          background: #5556FF !important;
        }

       }     
`;

export default myTheme;

/** @type {import('antd/lib').ThemeConfig} */
export const antdTheme = {
  token: {
    colorPrimary: '#5556FF',
    colorPrimaryHover: '#5556FF',
    colorPrimaryActive: '#5556FF',
    colorPrimaryBorder: '#B3B3FF',
    colorPrimaryBg: '#EEEEFF',
    colorSuccess: '#52C41A',
    colorSuccessBorder: '#B7EB8F',
    colorSuccessBg: '#F6FFED',
    colorWarning: '#FAAD14',
    colorWarningBorder: '#FFE58F',
    colorWarningBg: '#FFFBE6',
    colorError: '#F5222D',
    colorErrorBorder: '#FFA39E',
    colorErrorBg: '#FFF1F0',
    fontFamily: 'Roboto, PingFang SC, Segoe UI Emoji, Segoe UI Symbol',
    layoutHeaderBackground: '#233456',
    menuItemHeight: '48px',
    menuInlineToplevelItemHeight: '48px',
    alertInfoBgColor: '#e6f7ff',
    alertInfoBorderColor: '#91d5ff',
    alertInfoIconColor: '#1890f0',
    inputBg: '#ffffff',
    formItemMarginBottom: '0px',
    colorLink: '#5556FF',
    headerBorderBottom: '1px solid #f0f0f0',
  },
};
